import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { shallowEqual, useDispatch, useSelector } from "react-redux";

// Helper for date formatting
import moment from "moment";

// react-to-print
import { useReactToPrint } from "react-to-print";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const numberToWordsTR = (number) => {
  if (typeof number !== "number") {
    return "";
  }

  const fixedNumber = number.toFixed(2);
  const [liraPart, kurusPart] = fixedNumber.split(".");

  const birler = [
    "",
    "BİR",
    "İKİ",
    "ÜÇ",
    "DÖRT",
    "BEŞ",
    "ALTI",
    "YEDİ",
    "SEKİZ",
    "DOKUZ",
  ];
  const onlar = [
    "",
    "ON",
    "YİRMİ",
    "OTUZ",
    "KIRK",
    "ELLİ",
    "ALTMIŞ",
    "YETMİŞ",
    "SEKSEN",
    "DOKSAN",
  ];
  const basamaklar = ["", "BİN", "MİLYON", "MİLYAR", "TRİLYON"];

  const convertThreeDigits = (numStr) => {
    let text = "";
    const n = numStr.padStart(3, "0");

    const yuz = parseInt(n[0], 10);
    const on = parseInt(n[1], 10);
    const bir = parseInt(n[2], 10);

    if (yuz > 0) {
      text += (yuz === 1 ? "" : birler[yuz]) + " YÜZ ";
    }
    if (on > 0) {
      text += onlar[on] + " ";
    }
    if (bir > 0) {
      text += birler[bir] + " ";
    }
    return text.trim();
  };

  let liraText = "";
  if (liraPart === "0") {
    liraText = "SIFIR";
  } else {
    const chunks = [];
    let tempLira = liraPart;
    while (tempLira.length > 0) {
      chunks.push(tempLira.slice(-3));
      tempLira = tempLira.slice(0, -3);
    }

    liraText = chunks
      .map((chunk, index) => {
        if (chunk === "000") return "";
        let chunkText = convertThreeDigits(chunk);
        if (chunkText === "BİR" && index === 1) {
          chunkText = "";
        }
        return chunkText + " " + basamaklar[index];
      })
      .reverse()
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }

  let kurusText = "";
  if (kurusPart === "00") {
    kurusText = "SIFIR";
  } else {
    kurusText = convertThreeDigits(kurusPart.padStart(3, "0"));
  }

  return `${liraText} TÜRK LİRASI ${kurusText} KURUŞ`;
};

const ReceiptViewModal = ({ rowData, isOpen, toggle }) => {
  const dispatch = useDispatch();
  const componentRef = useRef(null);
  const onBeforePrintResolve = useRef(null);

  // States
  const [loading, setLoading] = useState(false);

  const paymentAmountInWords = rowData?.Price
    ? numberToWordsTR(rowData.Price)
    : "";

  const mockReceiptDetails = {
    receiptNo: "51288",
    maskedCardNo: "5243 **** **** 7972",
    maskedCardHolder: "MUR********",
    is3DSecure: "Hayır",
    dealerNo: "17699",
  };

  const receiptDetails = mockReceiptDetails;

  const fetchData = async () => {
    try {
    } catch (error) {
      console.error("Failed to fetch receipt details:", error);
    }
  };

  useEffect(() => {
    if (isOpen && rowData) {
      fetchData();
    }
  }, [isOpen, rowData, dispatch]);

  const handleOnAfterPrint = useCallback(() => setLoading(false), []);
  const handleOnBeforePrint = useCallback(() => {
    setLoading(true);

    return new Promise((resolve) => {
      onBeforePrintResolve.current = resolve;

      setTimeout(() => {
        setLoading(false);
        resolve();
      }, 2000);
    });
  }, [setLoading]);

  useEffect(() => {
    if (typeof onBeforePrintResolve.current === "function") {
      onBeforePrintResolve.current();
    }
  }, [onBeforePrintResolve.current]);

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Tahsilat-Makbuzu-${rowData.AggrementNo}`,
    onAfterPrint: handleOnAfterPrint,
    onBeforePrint: handleOnBeforePrint,
  });

  const handleDownloadPdf = async () => {
    if (!componentRef.current) return;

    setLoading(true);
    try {
      const canvas = await html2canvas(componentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

      const fileName = `Tahsilat_Makbuzu_${rowData.AggrementNo}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("PDF indirme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <ModalHeader
        className="modal-title"
        style={{
          backgroundColor: "whitesmoke",
          paddingBottom: "10px",
        }}
        id="myLargeModalLabel"
        toggle={toggle}
      >
        Tahsilat Makbuzu - {rowData.AggrementNo}
      </ModalHeader>

      <ModalBody
        style={{
          display: "flex",
          justifyContent: "center",
          maxHeight: "calc(90vh - 100px)",
          overflowY: "auto",
          overflowX: "auto",
          backgroundColor: "whitesmoke",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <div
            ref={componentRef}
            style={{
              width: "210mm",
              minHeight: "297mm",
              padding: "25px",
              backgroundColor: "#ffffff",
              color: "#000",
              pageBreakBefore: "always",
            }}
          >
            <h1
              style={{
                color: "#000",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "20px",
                letterSpacing: "1px",
                marginBottom: "30px",
              }}
            >
              TAHSİLAT MAKBUZU
            </h1>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ textAlign: "left" }}>
                  <th
                    style={{ padding: "5px", fontWeight: "bold", width: "20%" }}
                  >
                    SÖZLEŞME NO
                  </th>
                  <th
                    style={{ padding: "5px", fontWeight: "bold", width: "30%" }}
                  >
                    HİZMET VERİLEN ADI/SOYADI
                  </th>
                  <th
                    style={{ padding: "5px", fontWeight: "bold", width: "30%" }}
                  >
                    ACENTE
                  </th>
                  <th
                    style={{ padding: "5px", fontWeight: "bold", width: "20%" }}
                  >
                    TAHSİLAT TARİHİ
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "5px", borderTop: "1px solid #ccc" }}>
                    {rowData.AggrementNo}
                  </td>
                  <td style={{ padding: "5px", borderTop: "1px solid #ccc" }}>
                    {rowData.CustomerName}
                  </td>
                  <td style={{ padding: "5px", borderTop: "1px solid #ccc" }}>
                    {rowData.AgencyName}
                  </td>
                  <td style={{ padding: "5px", borderTop: "1px solid #ccc" }}>
                    {moment(rowData.IssuedTime).format("DD.MM.YYYY")}
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              style={{
                marginTop: "26px",
                borderTop: "1px solid #eee",
                paddingTop: "20px",
                fontSize: "12px",
                lineHeight: "1.8",
              }}
            >
              <p
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "5px 0",
                }}
              >
                <strong>ÖDEME TARİHİ:</strong>{" "}
                <span>{moment(rowData.IssuedTime).format("DD.MM.YYYY")}</span>
              </p>
              <p
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "5px 0",
                }}
              >
                <strong>ÖDEME TUTARI:</strong>{" "}
                <span>
                  {new Intl.NumberFormat("tr-TR", {
                    style: "currency",
                    currency: "TRY",
                  }).format(rowData.Price)}
                </span>
              </p>
              <p
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "5px 0",
                }}
              >
                <strong>ÖDEME TUTARI (YAZI İLE):</strong>{" "}
                <span>{paymentAmountInWords}</span>
              </p>
              {rowData?.CardNumber && (
                <p
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "5px 0",
                  }}
                >
                  <strong>KREDİ KARTI NO:</strong>{" "}
                  <span>{rowData.CardNumber}</span>
                </p>
              )}
              {rowData?.CardHolderName && (
                <p
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "5px 0",
                  }}
                >
                  <strong>KART SAHİBİ:</strong>{" "}
                  <span>{rowData.CardHolderName}</span>
                </p>
              )}
            </div>

            <div
              style={{
                marginTop: "26px",
                borderTop: "1px solid #eee",
                paddingTop: "20px",
                fontSize: "12px",
                lineHeight: "1.6",
              }}
            >
              <p>
                <strong>Sayın Müşterimiz,</strong>
              </p>
              <p>
                {rowData.AggrementNo} nolu hizmet sözleşmenizin bedeli{" "}
                {new Intl.NumberFormat("tr-TR", {
                  minimumFractionDigits: 2,
                }).format(rowData.Price)}{" "}
                TL olarak tahsil edilmiştir.
              </p>
              {rowData?.PaymentType === 0 && (
                <p>
                  İşlem tutarınız kredi kartı ekstrenize tek çekim olmak üzere
                  toplamda {paymentAmountInWords} olarak yansıyacaktır.
                </p>
              )}
              <p>
                Tahsil makbuzlarının hizmet sürenizin bitimine kadar şirketimiz
                ile olan işlemlerinizde ibraz edilmek üzere saklanması
                gerekmektedir.
              </p>
            </div>

            <div
              style={{
                fontSize: "12px",
              }}
            >
              <p style={{ margin: "2px 0", fontWeight: "bold" }}>
                GLOBAL YOL YARDIM
              </p>
              <p style={{ margin: "2px 0" }}>+90 850 242 70 42</p>
              <p style={{ margin: "2px 0" }}>info@globalyolyardim.com</p>
              <p style={{ margin: "2px 0" }}>www.globalyolyardim.com</p>
              <p style={{ margin: "2px 0" }}>
                Ziyabey Caddesi Irmak Sokak Kızılcık Plaza No:624/5
                Balgat/ANKARA
              </p>
            </div>

            {/* <div
              style={{
                marginTop: "26px",
                borderTop: "1px solid #eee",
                paddingTop: "20px",
              }}
            ></div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "20px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "13px",
              }}
            >
              <div style={{ width: "48%" }}>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>İŞLEM TARİHİ:</strong>{" "}
                  <span>{moment(rowData.IssuedTime).format("DD.MM.YYYY")}</span>
                </p>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>KREDİ KARTI NO:</strong>{" "}
                  <span>{receiptDetails.maskedCardNo}</span>
                </p>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>KK SAHİBİ:</strong>{" "}
                  <span>{receiptDetails.maskedCardHolder}</span>
                </p>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>3D TAHSİLAT:</strong>{" "}
                  <span>{receiptDetails.is3DSecure}</span>
                </p>
              </div>
              <div style={{ width: "48%" }}>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>BAYİ NO:</strong>{" "}
                  <span>{receiptDetails.dealerNo}</span>
                </p>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>SÖZLEŞME NO:</strong>{" "}
                  <span>{rowData.AggrementNo}</span>
                </p>
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>PRİM TUTARI:</strong>{" "}
                  <span>
                    {new Intl.NumberFormat("tr-TR", {
                      style: "currency",
                      currency: "TRY",
                    }).format(rowData.Price)}
                  </span>
                </p>
              </div>
            </div> */}

            {/* <div style={{ fontSize: "12px", marginTop: "20px" }}>
              <p>
                GLOBAL YOL YARDIM hizmet sözleşmesi prim tutarlarını yukarıda
                belirtilen kredi kartı hesabından tahsil etmeye yetkilidir.
              </p>
            </div> */}

            <div style={{ marginTop: "46px", fontSize: "12px" }}>
              <p>
                <strong>Tarih:</strong> ........ / ........ / .................
              </p>
              <p style={{ marginTop: "10px" }}>
                <strong>İmza:</strong>
              </p>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: "whitesmoke",
        }}
      >
        <Button
          className="btn btn-sm mt-3 me-2"
          color="success"
          onClick={handleDownloadPdf}
          disabled={loading}
        >
          {loading ? "Hazırlanıyor..." : "PDF İndir"}
        </Button>
        <Button
          className="btn btn-sm mt-3"
          color="primary"
          onClick={printFn}
          disabled={loading}
        >
          {loading ? "Hazırlanıyor..." : "PDF Yazdır"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReceiptViewModal;
